import React, { useState } from "react";
import Autosuggest from "react-autosuggest";

/**
 * AutoComplete component that provides a searchable dropdown with suggestions
 * Uses react-autosuggest library to provide typeahead functionality
 *
 * @param {Object} props - Component props
 * @param {Array} props.options - Array of available options to search through
 * @param {Object} props.properties - Current form properties/values
 * @param {string} props.property - The property key this component manages
 * @param {Object} props.field - Field configuration object
 * @param {Function} props.updateProperties - Callback to update parent component properties
 */
const AutoComplete = ({
    options,
    properties,
    property,
    field,
    updateProperties,
}) => {

    // State to store current suggestions based on user input
    const [suggestions, setSuggestions] = useState([]);

    // State to store the current search/input value
    // Initialize with the display name of the current selected value
    const [searchValue, setSearchValue] = useState(
        getValue(properties[property]),
    );

    /**
     * Extract the value from a suggestion object for form submission
     * @param {Object} suggestion - The selected suggestion object
     * @returns {*} The value to be used in form
     */
    const getSuggestionValue = (suggestion) => suggestion.value;

    /**
     * Render function for individual suggestion items in the dropdown
     * @param {Object} suggestion - The suggestion object to render
     * @returns {JSX.Element} The rendered suggestion
     */
    const renderSuggestion = (suggestion) => <div>{suggestion.name}</div>;

    /**
     * Convert a stored value back to its display name
     * Used to show the human-readable name instead of the internal value
     * @param {*} value - The stored value (usually numeric)
     * @returns {string} The display name or empty string if not found
     */
    function getValue(value) {
        const inputValue = parseInt(value);
        let foundoption = options.find((option) => {
            return option.value === inputValue;
        });
        return foundoption ? foundoption.name : "";
    }

    // Configuration object for the Autosuggest input field
    // This gets passed directly to the underlying input component
    const inputProps = {
        placeholder: "Type a programming language",
        value: searchValue,
        onChange: onChange,
    };

    /**
     * Filter function to calculate suggestions based on user input
     * Performs case-insensitive prefix matching against option names
     * @param {string} value - The current input value to match against
     * @returns {Array} Filtered array of matching options
     */
    const getSuggestions = (value) => {
        const inputValue = value.trim().toLowerCase();
        const inputLength = inputValue.length;

        // Return empty array if no input, otherwise filter options by name prefix
        return inputLength === 0
            ? []
            : options.filter(
                  (lang) =>
                      lang.name.toLowerCase().slice(0, inputLength) ===
                      inputValue,
              );
    };

    /**
     * Handle changes to the input field value
     * Updates the local searchValue state when user types
     * @param {Object} params - Change event parameters
     * @param {string} params.newValue - The new input value
     * @param {string} params.method - Method that triggered the change
     */
    function onChange({ newValue, method }) {
        setSearchValue(newValue);
    }

    /**
     * Called when autosuggest needs to update the suggestions list
     * Triggered when user types and suggestions should be recalculated
     * @param {Object} params - Request parameters
     * @param {string} params.value - The current input value to generate suggestions for
     */
    function onSuggestionsFetchRequested({ value }) {
        setSuggestions(getSuggestions(value));
    }

    /**
     * Called when autosuggest should clear all suggestions
     * Typically triggered when input loses focus or user selects a suggestion
     */
    function onSuggestionsClearRequested() {
        setSuggestions([]);
    }

    return (
        <div className={"row forminput mb-2"}>
            <div className="col-12 input-group">
                <div className="position-relative dropdown">
                    <Autosuggest
                        suggestions={suggestions}
                        onSuggestionsFetchRequested={
                            onSuggestionsFetchRequested
                        }
                        onSuggestionsClearRequested={
                            onSuggestionsClearRequested
                        }
                        getSuggestionValue={getSuggestionValue}
                        renderSuggestion={renderSuggestion}
                        inputProps={inputProps}
                    />
                </div>
            </div>
        </div>
    );
};

export default AutoComplete;
